import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import * as Router from "react-router-dom";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { FeatherIcon } from "component/shared/feather-icon";
import { ColdFusionIframe } from "component/shared/iframe/coldfusion";
import { Table, Column, Sort } from "component/shared/table";
import { PageProps } from "shared/page-props";
import { RootState } from "redux/store";
import { fetchActiveListings } from "redux/slice/active-listings";
import { ActiveListingResource } from "api/resource/active-listing";
import { coldFusionReplatform } from "shared/env";
import * as paths from "routes/paths";
import { styles } from "./style";

interface Props extends PageProps, Router.RouteComponentProps, WithStyles<typeof styles> {}

const adminTitle = "Active Listings";
const agentTitle = "Manage Listings";

// CF Address line 2 = GetToken(StreetNumber,1,".") + " " + UCase(StreetName): street number truncated
// at the first ".", street name upper-cased. Built from the raw streetNumber/streetName fields (the
// composed `address` string includes the unit number + city line, so it can't reproduce this exactly).
const streetLine = (listing: ActiveListingResource): string => {
	const streetNo = (listing.streetNumber || "").split(".")[0];
	return `${streetNo} ${(listing.streetName || "").toUpperCase()}`.trim();
};

const formatPrice = (price: number | null): string => (price == null ? "" : `$${price.toLocaleString()}`);

const ColdFusionFallback: React.FC<Props> = ({ user }) => {
	const title = user.admin ? adminTitle : agentTitle;
	const url = user.admin ? "/listingseditor.cfm" : "/agentprop.cfm";
	return (
		<DashboardLayout
			permitted={user.admin || (user.type.agent && user.permissions.modifyListings)}
			title={title}
			header={
				<Mui.Typography variant="h1">
					<FeatherIcon>
						<Icons.Home />
					</FeatherIcon>
					{title}
				</Mui.Typography>
			}
			iframe={<ColdFusionIframe url={url} />}
		/>
	);
};

const ListingsActiveBase: React.FC<Props> = ({ user, classes = {}, location, history }) => {
	const dispatch = useDispatch();
	const listings = useSelector((state: RootState) => state.activeListings.listings);

	const title = user.admin ? adminTitle : agentTitle;
	// CF FeatureAgentOffice gate → admin "Listing Agent" column (broker/office accounts only).
	const showListingAgent = user.admin && !!user.permissions.broker;

	const [search, setSearch] = useState("");
	const [sort, setSort] = useState<Sort>({ id: "address", desc: false });
	const [hasLoaded, setHasLoaded] = useState(false);
	const [bannerOpen, setBannerOpen] = useState(false);
	const [bannerMessage, setBannerMessage] = useState("");
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: "" });

	useEffect(() => {
		(async () => {
			try {
				// F4: .unwrap() surfaces a load failure via the error snackbar.
				await dispatch(fetchActiveListings({ admin: user.admin, agentId: user.agentId }) as any).unwrap();
			} catch{
				setSnackbar({ open: true, message: "Failed to load listings" });
			} finally {
				setHasLoaded(true);
			}
		})();
	}, [dispatch, user.admin, user.agentId]);

	// Success banner from the Message query param (matches CF after returning from the edit page).
	useEffect(() => {
		const message = new URLSearchParams(location.search).get("Message");
		if (message) {
			setBannerMessage(message);
			setBannerOpen(true);
		}
	}, [location.search]);

	// Navigate to the routed editor (listings-active/edit.tsx). The AllDesc override row is keyed by
	// (boardId, listingNumber); a row without a board has no editable override, so the entry point is a
	// no-op when boardId is missing.
	const openEditor = (listing: ActiveListingResource) => {
		if (listing.boardId == null) {
			return;
		}
		history.push(
			Router.generatePath(paths.listingsActiveEdit, {
				boardId: listing.boardId,
				listingNumber: listing.listingNumber,
			})
		);
	};

	const columns: Column<ActiveListingResource>[] = [
		{
			id: "address",
			title: "Address",
			value: (listing) => (
				<Mui.Link
					component="button"
					type="button"
					onClick={() => openEditor(listing)}
					data-testid={`active-edit-${listing.listingNumber}`}
					className={classes.addressLink}
				>
					<Mui.Typography variant="body2" component="span" display="block" className={classes.cityLabel}>
						{streetLine(listing)}
					</Mui.Typography>
					<Mui.Typography variant="body2" component="span" display="block">
						{(listing.city || "").toUpperCase()}
					</Mui.Typography>
				</Mui.Link>
			),
			sort: (a, b) =>
				(a.city || "").localeCompare(b.city || "") || streetLine(a).localeCompare(streetLine(b)),
		},
		{
			id: "price",
			title: "Price",
			value: (listing) => formatPrice(listing.listPrice),
			sort: (a, b) => (a.listPrice || 0) - (b.listPrice || 0),
		},
		{
			id: "mlsInfo",
			title: "MLS Info",
			value: (listing) =>
				listing.boardId != null ? (
					<>
						{listing.boardShortName}:
						<br />
						{listing.listingNumber}
					</>
				) : (
					""
				),
			sort: (a, b) => (a.listingNumber || "").localeCompare(b.listingNumber || ""),
		},
		{
			id: "listingAgent",
			title: "Listing Agent",
			hidden: !showListingAgent,
			value: (listing) => listing.listingAgentName || "",
			sort: (a, b) => (a.listingAgentName || "").localeCompare(b.listingAgentName || ""),
		},
		{
			id: "type",
			title: "Type",
			hidden: user.admin,
			value: (listing) => listing.propertyTypeLabel || listing.propertyTypeDisplay || "",
			sort: (a, b) =>
				(a.propertyTypeLabel || a.propertyTypeDisplay || "").localeCompare(
					b.propertyTypeLabel || b.propertyTypeDisplay || ""
				),
		},
		{
			id: "pageViews",
			title: "Page Views",
			value: (listing) => listing.pageViews || 0,
			sort: (a, b) => (a.pageViews || 0) - (b.pageViews || 0),
		},
		{
			id: "action",
			title: "Action",
			align: "right",
			value: (listing) =>
				listing.listingPageUrl ? (
					<Mui.Tooltip title="View listing on your website">
						<Mui.IconButton
							component="a"
							href={listing.listingPageUrl}
							target="_blank"
							rel="noopener noreferrer"
							size="small"
							aria-label="View listing on your website"
							data-testid={`active-show-${listing.listingNumber}`}
						>
							<FeatherIcon fontSize="small">
								<Icons.ExternalLink />
							</FeatherIcon>
						</Mui.IconButton>
					</Mui.Tooltip>
				) : null,
		},
	];

	// CF's list uses a client-side DataTables search over the rendered rows; match it with a
	// case-insensitive filter across address + MLS number.
	const filteredListings = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) {
			return listings;
		}
		return listings.filter((l) =>
			[streetLine(l), l.city, l.listingNumber, l.boardShortName, l.listingAgentName, l.propertyTypeLabel]
				.filter(Boolean)
				.join(" ")
				.toLowerCase()
				.includes(q)
		);
	}, [listings, search]);

	const renderBody = () => {
		if (!hasLoaded) {
			return (
				<Mui.Box display="flex" justifyContent="center" p={4}>
					<Mui.CircularProgress />
				</Mui.Box>
			);
		}
		if (listings.length === 0) {
			return <MuiAlert severity="info">No Current Active Listings.</MuiAlert>;
		}
		return (
			<Mui.Grid container direction="column" spacing={2}>
				<Mui.Grid item className={classes.headerRow}>
					<Mui.Typography variant="h6">Listings ({listings.length})</Mui.Typography>
				</Mui.Grid>
				<Mui.Grid item>
					<Mui.TextField
						variant="outlined"
						size="small"
						placeholder="Search address or MLS #"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						InputProps={{
							startAdornment: (
								<Mui.InputAdornment position="start">
									<FeatherIcon fontSize="small">
										<Icons.Search />
									</FeatherIcon>
								</Mui.InputAdornment>
							),
						}}
						inputProps={{ "data-testid": "active-search" }}
					/>
				</Mui.Grid>
				<Mui.Grid item>
					{filteredListings.length === 0 ? (
						<Mui.Typography variant="body2" color="textSecondary">
							No listings match "{search}".
						</Mui.Typography>
					) : (
						<Table items={filteredListings} columns={columns} sort={sort} onSortChange={setSort} />
					)}
				</Mui.Grid>
			</Mui.Grid>
		);
	};

	return (
		<DashboardLayout
			permitted={user.admin || (user.type.agent && user.permissions.modifyListings)}
			title={title}
			header={
				<Mui.Typography variant="h1">
					<FeatherIcon>
						<Icons.Home />
					</FeatherIcon>
					{title}
				</Mui.Typography>
			}
		>
			{bannerOpen && (
				<MuiAlert severity="success" className={classes.banner} onClose={() => setBannerOpen(false)}>
					{bannerMessage}
				</MuiAlert>
			)}

			<FullPageCard>{renderBody()}</FullPageCard>

			<Mui.Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
			>
				<MuiAlert severity="error">
					<Mui.Typography>{snackbar.message}</Mui.Typography>
				</MuiAlert>
			</Mui.Snackbar>
		</DashboardLayout>
	);
};

const ListingsActive = withStyles(ListingsActiveBase, styles);

const ListingsActivePageBase: React.FC<Props> = (props) => {
	if (!coldFusionReplatform) {
		return <ColdFusionFallback {...props} />;
	}
	return <ListingsActive {...props} />;
};

export const ListingsActivePage = withStyles(ListingsActivePageBase, styles);
